import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaBrain, FaBook, FaChartLine, FaStar } from 'react-icons/fa';
import { useAuth } from '../../../context/AuthContext';
import { habitService } from '../../../services/habit.service';
import { bookService } from '../../../services/book.service';
import { skillService } from '../../../services/skill.service';
import { db } from '../../../firebase';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import './Summary.css';

const GrowthSummary = () => {
  const [stats, setStats] = useState({
    habits: {
      total: 0,
      completed: 0
    },
    books: {
      reading: 0,
      completed: 0
    },
    skills: []
  });
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      // Load habits
      const loadHabits = async () => {
        const habits = await habitService.getHabits(user.uid);
        setStats(prev => ({
          ...prev,
          habits: {
            total: habits.length,
            completed: habits.filter(h => h.completedDates?.length > 0).length
          }
        }));
      };
      loadHabits();

      // Load books
      const loadBooks = async () => {
        const books = await bookService.getUserLibrary(user.uid);
        setStats(prev => ({
          ...prev,
          books: {
            reading: books.filter(b => b.status === 'reading').length,
            completed: books.filter(b => b.status === 'completed').length,
            total: books.length
          }
        }));
      };
      loadBooks();

      // Load skills
      const loadSkills = async () => {
        const skillsRef = collection(db, 'skills');
        const q = query(
          skillsRef,
          where('userId', '==', user.uid),
          orderBy('progress', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const skills = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setStats(prev => ({
          ...prev,
          skills: skills.map(skill => ({
            name: skill.name,
            progress: skill.progress,
            mastered: skill.mastered
          }))
        }));
      };
      loadSkills();
    }
  }, [user]);

  const masteredSkills = stats.skills.filter(skill => skill.mastered).length;

  return (
    <div className="summary-card">
      <div className="summary-header">
        <h2>Growth</h2>
        <FaBrain className="summary-icon" />
      </div>

      <div className="summary-stats">
        <div className="stat-item">
          <div className="stat-icon">
            <FaChartLine />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.habits.completed}</span>
            <span className="stat-label">Active Habits</span>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon">
            <FaBook />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.books.total}</span>
            <span className="stat-label">Books in Library</span>
          </div>
        </div>
      </div>

      <div className="skills-tracker">
        <div className="skills-header">
          <span className="skills-title">Skills Progress</span>
          <span className="skills-total">{masteredSkills} mastered</span>
        </div>
        <div className="skills-cards">
          {stats.skills.map((skill, index) => (
            <motion.div 
              key={skill.name}
              className={`skill-card ${skill.mastered ? 'mastered' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div className="skill-card-header">
                <span className="skill-name">{skill.name}</span>
                {skill.mastered && <FaStar className="mastery-icon" />}
              </div>
              <div className="skill-progress">
                <div 
                  className="skill-progress-fill"
                  style={{ width: `${skill.progress}%` }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GrowthSummary; 